import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import {
  ContactDetailsPanel,
  ContactDetailsPanelPending,
  ContactNotFoundPanel,
} from './-components/contact-details-panel';
import type { Id } from '@minty/shared/ids';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/tasks/$contactId')({
  pendingComponent: ContactDetailsPanelPending,
  remountDeps: ({ params }) => params.contactId,
  loader: async ({ context, params, preload }) => {
    const contactId = params.contactId as Id<'contacts'>;
    const { queryClient, trpc, trpcClient } = context;

    // Warmed, not awaited; the panel queries it again and reports its own failure.
    void queryClient
      .ensureQueryData(
        trpc.activities.feed.getLastEmailReplyTarget.queryOptions({
          contactId,
        }),
      )
      .catch(() => null);

    const [contact] = await Promise.all([
      queryClient.ensureQueryData(
        trpc.contacts.directory.getContact.queryOptions({ contactId }),
      ),
      queryClient.ensureQueryData(
        trpc.tasks.management.getOpenForContact.queryOptions({ contactId }),
      ),
    ]);

    /** Only when there is something to mark: revisiting a contact already
     *  read would cost a request the server then ignores. */
    if (!preload && contact && !contact.isRead) {
      void trpcClient.tasks.management.markContactRead
        .mutate({ contactId })
        .catch((error: unknown) => getPostHog()?.captureException(error));
    }
  },
  notFoundComponent: ContactNotFoundPanel,
  component: ContactRoute,
});

function ContactRoute() {
  const { contactId } = Route.useParams();
  const trpc = useTRPC();
  const { data: contact } = useSuspenseQuery(
    trpc.contacts.directory.getContact.queryOptions({
      contactId: contactId as Id<'contacts'>,
    }),
  );

  if (contact === null) {
    throw notFound();
  }

  return <ContactDetailsPanel contact={contact} />;
}
